'use client';

import { T } from '@/i18n/LocaleProvider';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MoreLink, PageSection } from '@/components';
import { MONO_FONT } from '@/theme';
import { useCountUp } from '@/hooks/useCountUp';

const KPIS = [
  { label: 'Score AEO', value: '64', countTo: 64, delta: '+8', down: false },
  {
    label: 'Cuota de mercado',
    value: '34%',
    countTo: 34,
    suffix: '%',
    delta: '+9 pts',
    down: false,
  },
  {
    label: 'Competencia',
    value: '#2',
    countTo: 2,
    prefix: '#',
    delta: '+1 puesto',
    down: false,
  },
  {
    label: 'Sentimiento',
    value: '82%',
    countTo: 82,
    suffix: '%',
    delta: '−2 pts',
    down: true,
  },
];

const CITE_ROWS = [
  {
    engine: 'ChatGPT',
    q: '"plataformas AEO" → Bison Agents en respuesta principal',
    pos: '#1',
  },
  {
    engine: 'Perplexity',
    q: '"medir IA generativa" → mención + link a manual de Bison',
    pos: '#2',
  },
  {
    engine: 'Gemini',
    q: '"AEO LATAM" → Bison Agents en lista comparativa',
    pos: '#1',
  },
  {
    engine: 'Claude',
    q: '"answer engine optimization español" → Bison como ejemplo',
    pos: '#3',
  },
];

const GAP_ROWS = [
  {
    desc: 'Identidad · Schema.org incompleto: faltan Organization y sameAs a perfiles oficiales',
    p: 'P1',
    crit: true,
  },
  {
    desc: 'Identidad · Nombre de marca inconsistente entre el sitio y los directorios',
    p: 'P1',
    crit: true,
  },
  {
    desc: 'Reputación · Perfil de reseñas sin reclamar y sin respuestas',
    p: 'P2',
    crit: false,
  },
  {
    desc: 'Editorial · Sin cobertura en medios de autoridad de la categoría',
    p: 'P2',
    crit: false,
  },
];

const B = '#e2e5e3'; // grey.200 — embedded in border shorthands to avoid MUI token resolution issues

export default function Dashboard() {
  const kpiGridRef = useRef(null);
  const kpiRefs = useCountUp(KPIS, kpiGridRef);

  return (
    <PageSection
      eyebrow="07 / Insights"
      title={
        <>
          <T>El </T>
          <span className="accent">
            <T>dashboard</T>
          </span>
          <T> que tu</T>{' '}
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <T>CMO querrá leer.</T>
        </>
      }
      lede="Lenguaje de negocio sobre datos técnicos. Cada métrica trazable hasta el prompt original. Exportable a tu deck del lunes y a tu junta del trimestre."
    >
      <Box
        sx={{ border: `1px solid ${B}`, borderRadius: 2, overflow: 'hidden' }}
      >
        {/* Browser bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            bgcolor: 'grey.50',
            borderBottom: `1px solid ${B}`,
            fontFamily: MONO_FONT,
            fontSize: '11px',
            color: 'text.muted',
            letterSpacing: '0.04em',
          }}
        >
          <Box sx={{ display: 'flex', gap: '5px' }}>
            {['#ff5f56', '#ffbd2e', '#27c93f'].map((c) => (
              <Box
                key={c}
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: c,
                }}
              />
            ))}
          </Box>
          <span>
            <T>app.bisonagents.com</T>
          </span>
          <Box
            component="span"
            sx={{
              ml: 'auto',
              letterSpacing: '0.06em',
              display: { xs: 'none', sm: 'inline' },
            }}
          >
            <T>CLIENTE · DEMO · MAYO 2026</T>
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '200px 1fr' },
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRight: { md: `1px solid ${B}` },
              borderBottom: { xs: `1px solid ${B}`, md: 'none' },
              py: 2.5,
              display: { xs: 'none', md: 'block' },
            }}
          >
            {[
              {
                head: 'Workspace',
                items: [
                  { label: 'Overview', active: true, pip: '●' },
                  { label: 'Cobertura', pip: '5' },
                  { label: 'Tráfico IA', pip: '18%' },
                  { label: 'Diagnóstico', pip: '62' },
                  { label: 'Mejoras', pip: '12' },
                  { label: 'Competidores', pip: '8' },
                ],
              },
              {
                head: 'Reportes',
                items: [
                  { label: 'Semanal' },
                  { label: 'Mensual' },
                  { label: 'Q2 — 2026' },
                ],
              },
            ].map((group, gi) => (
              <Box key={gi} sx={{ mt: gi > 0 ? 3 : 0 }}>
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'grey.600',
                    fontWeight: 600,
                    px: 2,
                    mb: 0.5,
                  }}
                >
                  <T>{group.head}</T>
                </Typography>
                {group.items.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      fontSize: '13px',
                      color: item.active ? 'text.primary' : 'grey.600',
                      fontWeight: item.active ? 600 : 400,
                      px: 2,
                      py: 0.875,
                      mx: 0.5,
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: item.active ? 'common.white' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <T>{item.label}</T>
                    {item.pip && (
                      <Box
                        component="span"
                        sx={{
                          fontFamily: MONO_FONT,
                          fontSize: '11px',
                          color: 'grey.600',
                          ml: 'auto',
                        }}
                      >
                        <T>{item.pip}</T>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* Main panel */}
          <Box
            sx={{
              p: { xs: 2.5, md: 3.5 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1.5,
                pb: 2,
                borderBottom: `1px solid ${B}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '22px',
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  color: 'text.primary',
                }}
              >
                <T>Overview · semana 15</T>
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '11px',
                  color: 'text.muted',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <T>Última lectura · hace 14 min</T>
              </Typography>
            </Box>

            {/* KPIs */}
            <Box
              ref={kpiGridRef}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)',
                },
                border: `1px solid ${B}`,
                borderRadius: 0.5,
                overflow: 'hidden',
              }}
            >
              {KPIS.map((kpi, i) => (
                <Box
                  key={kpi.label}
                  sx={{
                    p: { xs: '12px', md: '16px 20px' },
                    borderRight: {
                      xs: i % 2 === 0 ? `1px solid ${B}` : 'none',
                      sm: i < 3 ? `1px solid ${B}` : 'none',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '10.5px',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'text.muted',
                      mb: 1,
                    }}
                  >
                    <T>{kpi.label}</T>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '26px',
                      fontWeight: 400,
                      letterSpacing: '-0.02em',
                      color: 'text.primary',
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <Box
                      component="span"
                      ref={(el) => {
                        kpiRefs.current[i] = el;
                      }}
                    >
                      <T>{kpi.value}</T>
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '11px',
                        color: kpi.down ? 'error.main' : 'primary.darker',
                        ml: '6px',
                        letterSpacing: 0,
                      }}
                    >
                      <T>{kpi.delta}</T>
                    </Box>
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Two cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {/* Citations card */}
              <Box
                sx={{
                  border: `1px solid ${B}`,
                  borderRadius: 0.5,
                  p: { xs: '14px 12px', md: '18px 16px' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1.5,
                    pb: 1.25,
                    borderBottom: `1px solid ${B}`,
                  }}
                >
                  <T>Últimas citas detectadas</T>
                </Typography>
                {CITE_ROWS.map((row) => (
                  <Box
                    key={row.engine}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 28px',
                      gap: 1,
                      py: 1,
                      borderBottom: `1px solid ${B}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: MONO_FONT,
                        fontSize: '10px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: 'text.muted',
                        pt: '1px',
                      }}
                    >
                      <T>{row.engine}</T>
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'grey.700',
                        lineHeight: 1.45,
                      }}
                    >
                      <T>{row.q}</T>
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: MONO_FONT,
                        fontSize: '10px',
                        color: 'primary.darker',
                        bgcolor: 'rgba(0,107,89,0.08)',
                        px: '5px',
                        py: '2px',
                        borderRadius: '3px',
                        textAlign: 'center',
                        height: 'fit-content',
                      }}
                    >
                      <T>{row.pos}</T>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Gaps card */}
              <Box
                sx={{
                  border: `1px solid ${B}`,
                  borderRadius: 0.5,
                  p: { xs: '14px 12px', md: '18px 16px' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1.5,
                    pb: 1.25,
                    borderBottom: `1px solid ${B}`,
                  }}
                >
                  <T>Brechas críticas pendientes</T>
                </Typography>
                {GAP_ROWS.map((row) => (
                  <Box
                    key={row.desc}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 1,
                      py: 1,
                      borderBottom: `1px solid ${B}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'grey.700',
                        lineHeight: 1.45,
                      }}
                    >
                      <T>{row.desc}</T>
                    </Typography>
                    <Box
                      sx={{
                        fontFamily: MONO_FONT,
                        fontSize: '10px',
                        px: '8px',
                        py: '2px',
                        borderRadius: '3px',
                        height: 'fit-content',
                        bgcolor: row.crit ? '#fde2e2' : '#fef3c7',
                        color: row.crit ? '#b91c1c' : '#92400e',
                      }}
                    >
                      <T>{row.p}</T>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 4 }}>
        <MoreLink href="/plataforma" text="Cada métrica, explicada.">
          <T>Ver la plataforma</T>
        </MoreLink>
      </Box>
    </PageSection>
  );
}
